/**
 * OpenSeaMap seamark tile CACHE (native only).
 *
 * tiles.openseamap.org is slow, so on a device we route the seamark raster tiles
 * through a MapLibre custom protocol (seamarkcache://z/x/y.png). Each tile is
 * fetched from the live server ONCE via the OS HTTP stack (Filesystem.downloadFile,
 * CORS-free, off the WebView), written to Directory.Cache/seamark, and thereafter
 * served straight from disk — so repeat views and pan-backs are instant and we stop
 * hammering their server. A size-capped LRU (see seamarkTileCache.pure) keeps the
 * cache bounded, evicting the oldest tiles once it exceeds the cap.
 *
 * On web/dev there's no native HTTP stack, so we DON'T use the protocol at all:
 * seamarkTilesTemplate() returns the plain https URL and the WebView loads the
 * seamark tiles exactly as it does today. registerSeamarkCache() is a no-op there.
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import maplibregl from 'maplibre-gl';
import { SEAMARK_TILE_URL } from './seamarkOverlay';
import {
  SEAMARK_PROTOCOL,
  CAP_BYTES,
  LOW_WATER,
  parseSeamarkTileUrl,
  tileName,
  selectEvictions,
} from './seamarkTileCache.pure';

const CACHE_DIR = 'seamark';
const DIR = Directory.Cache;

/** true where the native cache is usable (a real Capacitor platform). */
function nativeCache(): boolean {
  return Capacitor.isNativePlatform();
}

/** The XYZ tiles template the seamark source should use here. */
export function seamarkTilesTemplate(): string {
  return nativeCache() ? `${SEAMARK_PROTOCOL}://{z}/{x}/{y}.png` : SEAMARK_TILE_URL;
}

/** Live OpenSeaMap tile URL for a z/x/y, from the shared template. */
function liveTileUrl(z: string, x: string, y: string): string {
  return SEAMARK_TILE_URL.replace('{z}', z).replace('{x}', x).replace('{y}', y);
}

function cachePath(name: string): string {
  return `${CACHE_DIR}/${name}`;
}

// In-memory LRU index (oldest-first): filename -> byte size. Mirrors what's on disk.
const lru = new Map<string, number>();
let totalBytes = 0;
let seeded = false;
const inflight = new Map<string, Promise<ArrayBuffer>>();

async function ensureDir(): Promise<void> {
  await Filesystem.mkdir({ path: CACHE_DIR, directory: DIR, recursive: true }).catch(() => {
    /* already exists */
  });
}

/** Seed the LRU from whatever tiles are already on disk (once per session). */
async function seed(): Promise<void> {
  if (seeded) return;
  seeded = true;
  try {
    const { files } = await Filesystem.readdir({ path: CACHE_DIR, directory: DIR });
    const infos = (files as Array<string | { name: string; size?: number; mtime?: number }>)
      .filter((f): f is { name: string; size?: number; mtime?: number } => typeof f !== 'string')
      .sort((a, b) => (a.mtime ?? 0) - (b.mtime ?? 0)); // oldest first
    for (const f of infos) {
      const size = typeof f.size === 'number' ? f.size : 0;
      lru.set(f.name, size);
      totalBytes += size;
    }
  } catch {
    /* no cache dir yet */
  }
}

function b64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

async function readTile(name: string): Promise<ArrayBuffer | null> {
  try {
    const { data } = await Filesystem.readFile({ path: cachePath(name), directory: DIR });
    if (typeof data === 'string') return b64ToArrayBuffer(data);
    return await (data as Blob).arrayBuffer();
  } catch {
    return null;
  }
}

/** Mark a tile most-recently-used (move to the end of the LRU order). */
function touch(name: string, size: number): void {
  if (lru.has(name)) lru.delete(name);
  lru.set(name, size);
}

/** Evict the oldest tiles from disk + index until back under the low-water mark. */
async function evictIfNeeded(): Promise<void> {
  const { names } = selectEvictions(lru, totalBytes, CAP_BYTES, LOW_WATER);
  for (const name of names) {
    const size = lru.get(name) ?? 0;
    lru.delete(name);
    totalBytes -= size;
    await Filesystem.deleteFile({ path: cachePath(name), directory: DIR }).catch(() => {
      /* already gone */
    });
  }
}

/** Download a tile to disk (CORS-free native HTTP), index it, and return its bytes. */
async function fetchAndCache(z: string, x: string, y: string, name: string): Promise<ArrayBuffer> {
  await ensureDir();
  await Filesystem.downloadFile({
    url: liveTileUrl(z, x, y),
    path: cachePath(name),
    directory: DIR,
    recursive: true,
  });
  const buf = await readTile(name);
  if (!buf) throw new Error('seamark tile unreadable after download');
  totalBytes += buf.byteLength;
  touch(name, buf.byteLength);
  await evictIfNeeded();
  return buf;
}

let registered = false;
/** Register the seamark cache protocol (native only; idempotent; no-op on web). */
export function registerSeamarkCache(): void {
  if (registered || !nativeCache()) return;
  registered = true;
  maplibregl.addProtocol(SEAMARK_PROTOCOL, async (params) => {
    const coords = parseSeamarkTileUrl(params.url);
    if (!coords) throw new Error(`bad seamark tile url ${params.url}`);
    const { z, x, y } = coords;
    const name = tileName(z, x, y);
    await seed();
    // Cache hit: serve from disk, refresh recency.
    if (lru.has(name)) {
      const cached = await readTile(name);
      if (cached) {
        touch(name, lru.get(name) ?? cached.byteLength);
        return { data: cached };
      }
      lru.delete(name); // fell out from under us — refetch
    }
    // Cache miss: de-dupe concurrent requests for the same tile.
    let p = inflight.get(name);
    if (!p) {
      p = fetchAndCache(z, x, y, name).finally(() => inflight.delete(name));
      inflight.set(name, p);
    }
    return { data: await p };
  });
}
